import { useEffect, useMemo, useState } from "react";
import { Page } from "@iq/models";
import TabView from "../../../CRM/components/TabView/TabView";
import "./IncidentDetail.css";
import VariableTicketContainer from "../../components/VariableTicketContainer/VariableTicketContainer"
import RelatedTickets from "../../components/VariableTicketPageTabs/RelatedTickets";
import WorkNotes from "../../components/VariableTicketPageTabs/WorkNotes";
import TicketActivity from "../../components/VariableTicketPageTabs/TicketActivity";
import useIncidentContext from "../../hooks/useIncidentContext";
import { lifecycleStatusOptions } from "../../services/lifecycle_service";

const INCIDENT_TABS = ["Related Tickets","Activity", "Work Notes"];

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

function formatTimestamp(value) {
	if (!value) {
		return "-";
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return String(value);
	}

	return parsed.toLocaleString();
}

function IncidentDetail({
	incidentId,
	initialIncident = null,
	clients = [],
	teamMembers = [],
	products = [],
	onBack,
	onIncidentUpdate,
	onIncidentRefresh,
	onAssignProblem,
	onUnlinkRelation,
	onOpenTicket,
	relationRefreshVersion = 0,
}) {
	const [incident, setIncident] = useState(initialIncident || null);
	const [statusDraft, setStatusDraft] = useState(initialIncident?.status || "new");
	const [assigneeDraft, setAssigneeDraft] = useState(
		initialIncident?.assigned_to_id != null ? String(initialIncident.assigned_to_id) : ""
	);
	const [priorityDraft, setPriorityDraft] = useState(initialIncident?.priority || "");
	const [productDraft, setProductDraft] = useState(
		initialIncident?.product_id != null ? String(initialIncident.product_id) : ""
	);
	const [resolutionSummaryDraft, setResolutionSummaryDraft] = useState(initialIncident?.resolution_summary || "");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [unlinkingRelationId, setUnlinkingRelationId] = useState(null);
	const [unlinkError, setUnlinkError] = useState("");
	const [activeTab, setActiveTab] = useState(INCIDENT_TABS[0]);
	const {
		context: incidentContext,
		loading: loadingIncidentContext,
		error: incidentContextError,
		refresh: refreshIncidentContext,
	} = useIncidentContext(incidentId);

	useEffect(() => {
		if (relationRefreshVersion > 0) {
			refreshIncidentContext();
		}
	}, [relationRefreshVersion, refreshIncidentContext]);

	useEffect(() => {
		setIncident(initialIncident || null);
	}, [initialIncident]);

	useEffect(() => {
		setStatusDraft(initialIncident?.status || "new");
		setAssigneeDraft(initialIncident?.assigned_to_id != null ? String(initialIncident.assigned_to_id) : "");
		setPriorityDraft(initialIncident?.priority || "");
		setProductDraft(initialIncident?.product_id != null ? String(initialIncident.product_id) : "");
		setResolutionSummaryDraft(initialIncident?.resolution_summary || "");
	}, [initialIncident?.status, initialIncident?.assigned_to_id, initialIncident?.priority, initialIncident?.product_id, initialIncident?.resolution_summary]);

	const statusOptions = lifecycleStatusOptions(incident?.status || statusDraft, incidentContext?.allowed_transitions);

	const clientMap = useMemo(
		() =>
			new Map(
				(Array.isArray(clients) ? clients : []).map((client) => [
					String(client?.id ?? ""),
					{
						name: client?.name || `Client #${client?.id ?? "?"}`,
						company: client?.company || client?.organization || "",
					},
				])
			),
		[clients]
	);

	const productMap = useMemo(
		() =>
			new Map(
				(Array.isArray(products) ? products : []).map((product) => [
					String(product?.id ?? ""),
					product?.name || product?.product_name || `Product #${product?.id ?? "?"}`,
				])
			),
		[products]
	);

	const teamMemberMap = useMemo(
		() =>
			new Map(
				(Array.isArray(teamMembers) ? teamMembers : []).map((member) => [
					String(member?.id ?? ""),
					member?.name || member?.email || `User #${member?.id ?? "?"}`,
				])
			),
		[teamMembers]
	);

	const assigneeOptions = useMemo(
		() =>
			(Array.isArray(teamMembers) ? teamMembers : [])
				.map((member) => ({
					id: member?.id,
					label: member?.name || member?.email || `User #${member?.id ?? "?"}`,
				}))
				.filter((option) => option.id != null),
		[teamMembers]
	);		

	const displayClient =
		incident?.client_id != null && incident?.client_id !== ""
			? clientMap.get(String(incident.client_id))?.name || `Client #${incident.client_id}`
			: "Unassigned";

	const displayCompany =
		incident?.client_id != null && incident?.client_id !== ""
			? clientMap.get(String(incident.client_id))?.company || ""
			: "";

	const displayProduct =
		incident?.product_id != null && incident?.product_id !== ""
			? productMap.get(String(incident.product_id)) || `Product #${incident.product_id}`
			: "Unassigned";

	const displayAssignee =
		incident?.assigned_to_id != null && incident?.assigned_to_id !== ""
			? teamMemberMap.get(String(incident.assigned_to_id)) || `User #${incident.assigned_to_id}`
			: "Unassigned";

	function resetDraftFields() {
		setSaveError("");
		setStatusDraft(incident?.status || "new");
		setAssigneeDraft(incident?.assigned_to_id != null ? String(incident.assigned_to_id) : "");
		setPriorityDraft(incident?.priority || "");
		setProductDraft(incident?.product_id != null ? String(incident.product_id) : "");
		setResolutionSummaryDraft(incident?.resolution_summary || "");
	}

	async function handleSaveFields() {
		if (!incidentId || !onIncidentUpdate) {
			return;
		}

		setSaving(true);
		setSaveError("");

		const payload = {
			status: statusDraft,
			assigned_to_id: assigneeDraft ? Number(assigneeDraft) : null,
			priority: priorityDraft,
			product_id: productDraft ? Number(productDraft) : null,
			resolution_summary: resolutionSummaryDraft,
		};

		try {
			const updated = await onIncidentUpdate(incidentId, payload);
			setIncident((prev) => ({ ...(prev || {}), ...(updated || {}) }));
			await refreshIncidentContext();
		} catch (error) {
			setSaveError(error?.message || "Failed to update incident");
		} finally {
			setSaving(false);
		}
	}

	async function handleUnlink({ relationId, ticket, ticketType }) {
		if (!relationId || !onUnlinkRelation) {
			return;
		}

		setUnlinkingRelationId(relationId);
		setUnlinkError("");
		try {
			await onUnlinkRelation({
				relationId,
				tickets: [
					{ type: "incident", ticket: incident },
					{ type: ticketType, ticket },
				],
			});
			await refreshIncidentContext();
		} catch (error) {
			setUnlinkError(error?.message || "Failed to unlink tickets");
		} finally {
			setUnlinkingRelationId(null);
		}
	}

	const hasUnsavedChanges =
		statusDraft !== (incident?.status || "new") ||
		assigneeDraft !== (incident?.assigned_to_id != null ? String(incident.assigned_to_id) : "") ||
		priorityDraft !== (incident?.priority || "") ||
		productDraft !== (incident?.product_id != null ? String(incident.product_id) : "") ||
		resolutionSummaryDraft !== (incident?.resolution_summary || "");
	const subtitle = [displayClient, displayCompany].filter(Boolean).join(" · ");
	const child1Data = {
		title: incident?.title || "Untitled Incident",
		subtitle: subtitle,
		pill: incident?.status || "new",
		fields: [
			{ label: "Incident ID:", value: incident?.number },
			{ label: "Product:", value: displayProduct },
			{ label: "Occurred At:", value: formatTimestamp(incident?.occurred_at) },
			{ label: "Priority:", value: incident?.priority },
		],
	};

	const child1ActionButtons = [
		{
			key: "assign-problem",
			label: "Assign Problem",
			onClick: () => onAssignProblem?.(incident),
			visible: Boolean(incident?.id),
		},
	];

	const child2Data = {
		inputs: [
			// { label: "Incident ID:", value: incident?.id ?? "ID Not Found", type: "text", disabled: true },
			// { label: "Source:", value: incident?.source ?? "-", type: "text", disabled: true },
			// { label: "Priority:", value: priorityDraft, placeholder: incident?.priority ?? "Unassigned", type: "dropdown", children: PRIORITY_OPTIONS, disabled: false, onChange: (value) => setPriorityDraft(value) },
			// { label: "Product:", value: productDraft, placeholder: displayProduct, type: "dropdown", children: productMap, disabled: false, onChange: (value) => setProductDraft(value) },
			{ label: "Lifecycle Status:", value: statusDraft, placeholder: incident?.status ?? "Unassigned", type: "dropdown", children: statusOptions, disabled: false, onChange: (value) => setStatusDraft(value) },
			{ label: "Assigned To:", value: assigneeDraft, placeholder: displayAssignee, type: "dropdown", children: assigneeOptions, disabled: false, onChange: (value) => setAssigneeDraft(value) },
			// { label: "Occurred At:", value: formatTimestamp(incident?.occurred_at), type: "text", disabled: true },
			{ label: "Resolved At:", value: formatTimestamp(incident?.resolved_at), type: "text", disabled: true, },
			{ label: "Closed At:", value: formatTimestamp(incident?.closed_at), type: "text", disabled: true },
			{ label: "Resolution Summary:", value: resolutionSummaryDraft, placeholder: "Required before closing", type: "textarea", disabled: false, style: "grid-column: 1 / span 4;", onChange: (value) => setResolutionSummaryDraft(value) },
			{ label: "Description:", value: incident?.description ?? "No description", type: "textarea", disabled: true, style: "grid-column: 1 / span 4;" }
		]
	};

	const actionButtons = [
		{
			key: "cancel-changes",
			label: "Cancel",
			onClick: resetDraftFields,
			disabled: saving,
			variant: "secondary",
			visible: hasUnsavedChanges,
		},
		{
			key: "save-changes",
			label: saving ? "Saving..." : "Save Changes",
			onClick: handleSaveFields,
			disabled: saving,
			visible: hasUnsavedChanges,
		},
	];

	const views = {
		"Related Tickets": (
			<RelatedTickets
				data={incidentContext}
				loading={loadingIncidentContext}
				error={unlinkError || incidentContextError}
				onOpenTicket={onOpenTicket}
				onUnlink={handleUnlink}
				unlinkingRelationId={unlinkingRelationId}
			/>
		),
		Activity: <TicketActivity ticketType="incident" ticketId={incidentId} onOpenTicket={onOpenTicket} />,
		"Work Notes": <WorkNotes ticketType="incident" ticketId={incidentId} />,
	};

	const displayPageTitle = `Incident: ${incident?.number}`;

	return (
		<Page>
			{saveError ? <p style={{ color: "var(--color-danger)", marginBottom: 12 }}>{saveError}</p> : null}
			<VariableTicketContainer
				title="Incident Details"
				child1={child1Data}
				child1ActionButtons={child1ActionButtons}
				child2={child2Data}
				actionButtons={actionButtons}
				child3={ 
					<TabView
						tabs={INCIDENT_TABS}
						views={views}
						activeTab={activeTab}
						onTabChange={setActiveTab}
					/>
				}
			/>
		</Page>
	);
}

export default IncidentDetail;
